import { useState, Suspense } from "react";
import { ErrorBoundary } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { ExperimentTable } from "@/components/experiments/experiment-table";
import { ExperimentFilters } from "@/components/experiments/experiment-filters";
import { CreateExperimentDialog } from "@/components/experiments/create-experiment-dialog";
import { experimentListQueryOptions } from "@/hooks/use-experiments";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import type { ExperimentStatus } from "@/types/enums";

export default function ExperimentsPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ExperimentStatus>();

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            A/B 테스트 관리
          </h1>
          <p className="text-muted-foreground leading-normal">
            실험을 생성하고 상태를 관리합니다.
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          새 실험
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>실험 목록</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 px-4 md:px-6">
          <ExperimentFilters
            status={statusFilter}
            onStatusChange={setStatusFilter}
          />

          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
                <Suspense fallback={<TableLoadingFallback columnCount={6} />}>
                  <SuspenseQuery
                    {...experimentListQueryOptions(statusFilter)}
                  >
                    {({ data }) => <ExperimentTable data={data} />}
                  </SuspenseQuery>
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
        </CardContent>
      </Card>

      <CreateExperimentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </div>
  );
}
